import i18n from '@/utils/i18n';
import React, {
  MutableRefObject,
  useCallback,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { CatalogResultsHeader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/catalog-search-table/catalog-results-header';
import { ColumnId } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { createStyles } from '@/contexts/theme-context';
import {
  ESTIMATED_HEADER_HEIGHT,
  ESTIMATED_ROW_HEIGHT,
  ESTIMATED_SECTION_HEADER_HEIGHT,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-constants';
import { LayoutChangeEvent, View, ViewStyle } from 'react-native';
import { OnChangeFn, SortingState, TableState } from '@tanstack/react-table';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { type OrderEntryTableMeta } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { SalesPortalKeyboardScope } from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { Table } from '@experiences/components/table/table';
import { TableScrollHandle } from '@experiences/components/table/types';
import { useOrderEntryTableConfig } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-order-entry-table-config';
import { useOrderEntryTableRendering } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-order-entry-table-rendering';
import type { LastPurchaseInfoMap } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/fulfilled-date-transformer';

const getOrderGuideLabels = (row: OrderEntryTableRowData) =>
  row.orderGuideLabels;

// Multiplier for the bottom visibility buffer (in row heights).
// Matches OrderEntryTable - accounts for bottom bar covering rows.
const ROW_VISIBILITY_BUFFER_ROWS_MULTIPLIER = 3;

interface CatalogSearchTableProps {
  areSubstitutionsEnabled?: boolean;
  bodyContainerStyle?: ViewStyle;
  containerStyle?: ViewStyle;
  data: OrderEntryTableRowData[];
  emptyState: React.ReactNode;
  isActive?: boolean;
  isGrossProfitHidden?: boolean;
  isPriceEditingEnabled?: boolean;
  lastPurchaseInfoMap?: LastPurchaseInfoMap;
  minMaxQuantityEnabled?: boolean;
  onFocusedRowChange?: (rowData: OrderEntryTableRowData | null) => void;
  onOpenRestaurantSelector?: () => void;
  onPriceValidationError: (message: string) => void;
  onRowPress?: (rowData: OrderEntryTableRowData) => void;
  onSaveCart: () => void;
  onSavePriceEdit: (editor: PriceEditor, variantPackUUID: string) => void;
  onSwitchBuildReviewMode?: () => void;
  onToggleCheckout?: () => void;
  onTriggerSearch?: () => void;
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  quantityInputDisabled?: boolean;
  scrollHandleRef?: MutableRefObject<TableScrollHandle | null>;
  searchQuery: string;
}

/**
 * Table component specifically for catalog search results.
 *
 * Key differences from OrderEntryTable:
 * - No global filtering (server already filtered the results)
 * - Includes sticky section header showing result count and query
 * - Reuses column definitions via useOrderEntryTableConfig hook
 */
const CatalogSearchTableComponent: React.FC<CatalogSearchTableProps> = ({
  areSubstitutionsEnabled,
  bodyContainerStyle,
  containerStyle,
  data,
  emptyState,
  isActive = true,
  isGrossProfitHidden,
  isPriceEditingEnabled,
  lastPurchaseInfoMap,
  minMaxQuantityEnabled,
  onFocusedRowChange,
  onSaveCart,
  onOpenRestaurantSelector,
  onPriceValidationError,
  onRowPress,
  onSavePriceEdit,
  onSwitchBuildReviewMode,
  onToggleCheckout,
  onTriggerSearch,
  priceEditDisplayHeaders,
  quantityInputDisabled,
  scrollHandleRef,
  searchQuery,
}) => {
  const [styles, theme] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [, startTransition] = useTransition();

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (newSorting) => {
      startTransition(() => {
        setSorting(newSorting);
      });
    },
    [startTransition],
  );

  const { columnOrder, columnVisibility, columns, pinnedColumns } =
    useOrderEntryTableConfig({
      areSubstitutionsEnabled,
      data,
      getOrderGuideLabels,
      isGrossProfitHidden,
      isPriceEditingEnabled,
      lastPurchaseInfoMap,
      minMaxQuantityEnabled,
      onOpenRestaurantSelector,
      onPriceValidationError,
      onSaveCart,
      onSavePriceEdit,
      onSwitchBuildReviewMode,
      onToggleCheckout,
      onTriggerSearch,
      priceEditDisplayHeaders,
      quantityInputDisabled,
    });
  const { renderHeaderRow, renderRow } = useOrderEntryTableRendering({
    onRowPress,
  });

  const editableColumns = useMemo(
    () =>
      isPriceEditingEnabled
        ? [ColumnId.Quantity, ColumnId.Price, ColumnId.GpPercentage]
        : [ColumnId.Quantity],
    [isPriceEditingEnabled],
  );

  const meta = useMemo(
    (): OrderEntryTableMeta => ({
      areSubstitutionsEnabled: !!areSubstitutionsEnabled,
      isPriceEditingEnabled: !!isPriceEditingEnabled,
    }),
    [areSubstitutionsEnabled, isPriceEditingEnabled],
  );

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setLayoutWidth(width);
  }, []);

  const memoizedTableState = useMemo((): Partial<TableState> => {
    return { columnOrder, columnVisibility, sorting };
  }, [columnOrder, columnVisibility, sorting]);

  const sectionHeader = useMemo(
    () =>
      data.length > 0 ? (
        <CatalogResultsHeader count={data.length} query={searchQuery} />
      ) : undefined,
    [data.length, searchQuery],
  );

  if (!layoutWidth) {
    return (
      <View
        accessibilityLabel={i18n.t('sales_portal.order_entry.table.title')}
        accessibilityRole="progressbar"
        accessibilityState={{ busy: true, expanded: false }}
        accessible
        onLayout={handleLayout}
      />
    );
  }

  return (
    <View
      accessibilityLabel={i18n.t('sales_portal.order_entry.table.title')}
      onLayout={handleLayout}
      style={[styles.container, containerStyle]}
    >
      <Table
        bodyContainerStyle={bodyContainerStyle}
        cellPadding={[
          [theme.sk.spacing.spaceSmall, 0],
          ...Array(columns.length - 1).fill([
            theme.sk.spacing.spaceMedium,
            theme.sk.spacing.spaceMedium,
          ]),
        ]}
        columns={columns}
        data={data}
        editableColumns={editableColumns}
        emptyComponent={emptyState}
        estimatedHeaderHeight={ESTIMATED_HEADER_HEIGHT}
        estimatedRowHeight={ESTIMATED_ROW_HEIGHT}
        estimatedSectionHeaderHeight={ESTIMATED_SECTION_HEADER_HEIGHT}
        gutters={[0, theme.sk.spacing.spaceLarge]}
        initialState={{
          columnPinning: pinnedColumns,
        }}
        isActive={isActive}
        keyboardShortcutScope={SalesPortalKeyboardScope.ORDER_ENTRY_TABLE}
        layoutWidth={layoutWidth}
        meta={meta}
        onFocusedRowChange={onFocusedRowChange}
        onSortingChange={onSortingChange}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
        rowVisibilityBottomBufferMultiplier={
          ROW_VISIBILITY_BUFFER_ROWS_MULTIPLIER
        }
        scrollHandleRef={scrollHandleRef}
        sectionHeader={sectionHeader}
        state={memoizedTableState}
      />
    </View>
  );
};

export const CatalogSearchTable = React.memo(CatalogSearchTableComponent);

const useStyles = createStyles(() => ({
  container: {
    flex: 1,
  },
}));
